import React, { useState, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import {
  Search as SearchIcon,
  OpenInNew as OpenIcon,
  Add as AddIcon,
  LocationOn as LocationIcon,
  Science as ScienceIcon,
  BookmarkAdd as SaveIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material'
import { useQuery } from 'react-query'
import axios from 'axios'
import dayjs from 'dayjs'
import toast from 'react-hot-toast'

import LoadingSpinner from '../components/LoadingSpinner'

interface MatchedTrial {
  id: string
  nct_id: string
  eligibility_status: string
  created_at: string
  edited_at: string
}

interface Disease {
  id: string
  name: string
  isPrimaryDx: boolean
}

interface CTGovTrial {
  id: string
  title: string
  overallStatus: string
  briefSummary: string
  phases: string[]
  conditions: string[]
  locations: {
    facilityName: string
    city: string
    state: string
    country: string
  }[]
}

const TrialSearches: React.FC = () => {
  const [searching, setSearching] = useState(false)
  const [foundTrials, setFoundTrials] = useState<CTGovTrial[]>([])
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editingTrial, setEditingTrial] = useState<MatchedTrial | null>(null)
  const [addForm, setAddForm] = useState({
    nct_id: '',
    eligibility_status: 'not analyzed',
  })
  const [editForm, setEditForm] = useState({
    eligibility_status: 'not analyzed',
  })
  const [searchForm, setSearchForm] = useState({
    condition_term: '',
    phase: '',
    recruiting_only: true,
    limit_location: false,
    radius_miles: 50,
    location_input: '',
  })

  const getEligibilityColor = (status: string): "default" | "success" | "error" | "warning" => {
    switch (status) {
      case 'eligible': return 'success'
      case 'not eligible': return 'error'
      case 'potentially eligible': return 'warning'
      default: return 'default'
    }
  }

  const { data: matchedTrials, isLoading: trialsLoading, refetch } = useQuery<MatchedTrial[]>(
    'matchedTrials',
    async () => {
      const response = await axios.get('/health-data/matched-trials')
      return response.data.data || []
    }
  )

  const { data: diseases } = useQuery<Disease[]>(
    'primaryDiseases',
    async () => {
      const response = await axios.get('/health-data/diseases')
      const allDiseases = response.data.data || []
      const primaryDiseases = allDiseases.filter((d: any) => d.is_primary_dx === true)
      return primaryDiseases
    }
  )

  // Populate condition_term with primary diagnoses when diseases load
  useEffect(() => {
    if (diseases && diseases.length > 0 && !searchForm.condition_term) {
      const primaryConditions = diseases.map((d: any) => d.name).join(', ')
      setSearchForm(prev => ({
        ...prev,
        condition_term: primaryConditions
      }))
    }
  }, [diseases])

  const handleFormChange = (field: string, value: any) => {
    setSearchForm(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const geocodeLocation = async (location: string): Promise<{ lat: number, lon: number } | null> => {
    try {
      // Using a simple geocoding service (you may want to use Google Maps API or similar)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(location)}&limit=1`
      )
      const data = await response.json()
      if (data && data.length > 0) {
        return {
          lat: parseFloat(data[0].lat),
          lon: parseFloat(data[0].lon)
        }
      }
      return null
    } catch (error) {
      console.error('Geocoding error:', error)
      return null
    }
  }

  const handleSearch = async () => {
    if (!searchForm.condition_term.trim()) {
      toast.error('Please enter a condition to search for')
      return
    }

    try {
      setSearching(true)
      setFoundTrials([])
      
      // Build ClinicalTrials.gov API URL (same as iOS app)
      const baseURL = 'https://clinicaltrials.gov/api/v2/studies'
      const params = new URLSearchParams()
      
      // Add condition query (in quotes like iOS app)
      params.append('query.cond', `"${searchForm.condition_term}"`)
      params.append('pageSize', '100')
      
      // Add recruiting filter if enabled
      if (searchForm.recruiting_only) {
        params.append('filter.overallStatus', 'RECRUITING')
      }
      
      // Add location filter if enabled (with geocoding)
      if (searchForm.limit_location && searchForm.location_input) {
        const coords = await geocodeLocation(searchForm.location_input)
        if (coords) {
          params.append('filter.geo', `distance(${coords.lat},${coords.lon},${searchForm.radius_miles}mi)`)
        } else {
          toast.error('Could not geocode location, searching without location filter')
        }
      }
      
      // Call ClinicalTrials.gov API
      const apiUrl = `${baseURL}?${params.toString()}`
      
      const response = await fetch(apiUrl)
      const data = await response.json()
      
      // Parse trials from response (same structure as iOS app)
      const trials: CTGovTrial[] = (data.studies || []).map((study: any) => {
        const protocol = study.protocolSection
        return {
          id: protocol?.identificationModule?.nctId || '',
          title: protocol?.identificationModule?.briefTitle || protocol?.identificationModule?.officialTitle || '',
          overallStatus: protocol?.statusModule?.overallStatus || '',
          briefSummary: protocol?.descriptionModule?.briefSummary || '',
          phases: protocol?.designModule?.phases || [],
          conditions: protocol?.conditionsModule?.conditions || [],
          locations: (protocol?.contactsLocationsModule?.locations || []).map((loc: any) => ({
            facilityName: loc.facility || '',
            city: loc.city || '',
            state: loc.state || '',
            country: loc.country || '',
          }))
        }
      })
      
      setFoundTrials(trials)
      
      // Log the search to backend database
      await axios.post('/health-data/trial-searches', {
        conditionTerm: searchForm.condition_term,
        phase: searchForm.phase || null,
        recruitingOnly: searchForm.recruiting_only,
        limitLocation: searchForm.limit_location,
        radiusMiles: searchForm.limit_location ? searchForm.radius_miles : null,
        locationInput: searchForm.limit_location ? searchForm.location_input : null,
      })
      
      toast.success(`Found ${trials.length} clinical trials!`)
      
      // Refresh matched trials
      refetch()
    } catch (error: any) {
      console.error('Search error:', error)
      toast.error(error.message || 'Search failed')
    } finally {
      setSearching(false)
    }
  }

  const handleOpenTrial = (nctId: string) => {
    window.open(`https://clinicaltrials.gov/study/${nctId}`, '_blank')
  }

  const handleSaveAsMatched = async (trial: CTGovTrial) => {
    try {
      await axios.post('/health-data/matched-trials', {
        nct_id: trial.id, // Snake case to match backend validation
        eligibility_status: 'not analyzed', // Default status, can be updated later
      })
      
      toast.success(`Trial ${trial.id} saved to matched trials!`)
      refetch() // Refresh the matched trials list
    } catch (error: any) {
      console.error('Save trial error:', error)
      toast.error(error.response?.data?.error || 'Failed to save trial')
    }
  }

  const handleAddMatchedTrial = async () => {
    if (!addForm.nct_id.trim()) {
      toast.error('Please enter an NCT ID')
      return
    }

    // Validate NCT ID format
    if (!/^NCT[0-9]{8}$/.test(addForm.nct_id)) {
      toast.error('Invalid NCT ID format. Must be NCT followed by 8 digits (e.g., NCT12345678)')
      return
    }

    try {
      await axios.post('/health-data/matched-trials', {
        nct_id: addForm.nct_id,
        eligibility_status: addForm.eligibility_status,
      })
      
      toast.success(`Trial ${addForm.nct_id} added to matched trials!`)
      setAddDialogOpen(false)
      setAddForm({ nct_id: '', eligibility_status: 'not analyzed' })
      refetch() // Refresh the matched trials list
    } catch (error: any) {
      console.error('Add trial error:', error)
      toast.error(error.response?.data?.error || 'Failed to add trial')
    }
  }

  const handleEditMatchedTrial = (trial: MatchedTrial) => {
    setEditingTrial(trial)
    setEditForm({ eligibility_status: trial.eligibility_status })
    setEditDialogOpen(true)
  }

  const handleUpdateMatchedTrial = async () => {
    if (!editingTrial) return

    try {
      await axios.put(`/health-data/matched-trials/${editingTrial.id}`, {
        eligibility_status: editForm.eligibility_status,
      })
      
      toast.success(`Trial ${editingTrial.nct_id} updated!`)
      setEditDialogOpen(false)
      setEditingTrial(null)
      refetch() // Refresh the matched trials list
    } catch (error: any) {
      console.error('Update trial error:', error)
      toast.error(error.response?.data?.error || 'Failed to update trial')
    }
  }

  const handleDeleteMatchedTrial = async (trial: MatchedTrial) => {
    if (!confirm(`Are you sure you want to delete trial ${trial.nct_id}?`)) {
      return
    }

    try {
      await axios.delete(`/health-data/matched-trials/${trial.id}`)
      toast.success(`Trial ${trial.nct_id} deleted!`)
      refetch() // Refresh the matched trials list
    } catch (error: any) {
      console.error('Delete trial error:', error)
      toast.error(error.response?.data?.error || 'Failed to delete trial')
    }
  }

  if (trialsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <LoadingSpinner message="Loading matched trials..." />
      </Box>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          Clinical Trial Search
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Search for clinical trials and view your matched results
        </Typography>
      </Box>

      {/* Search Form */}
      <Card sx={{ mb: 4, bgcolor: 'grey.50' }}>
        <CardContent>
          <Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Condition or Disease"
                  placeholder="e.g., Type 2 Diabetes, Breast Cancer, Alzheimer's"
                  value={searchForm.condition_term}
                  onChange={(e) => handleFormChange('condition_term', e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Trial Phase"
                  value={searchForm.phase}
                  onChange={(e) => handleFormChange('phase', e.target.value)}
                >
                  <MenuItem value="">All Phases</MenuItem>
                  <MenuItem value="Phase 1">Phase 1</MenuItem>
                  <MenuItem value="Phase 2">Phase 2</MenuItem>
                  <MenuItem value="Phase 3">Phase 3</MenuItem>
                  <MenuItem value="Phase 4">Phase 4</MenuItem>
                </TextField>
              </Grid>
              
              <Grid item xs={12} md={2} display="flex" alignItems="center">
                <FormControlLabel
                  control={
                    <Switch
                      checked={searchForm.limit_location}
                      onChange={(e) => handleFormChange('limit_location', e.target.checked)}
                    />
                  }
                  label="Limit by Location"
                  sx={{ m: 0 }}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  label="Location (City, State or ZIP)"
                  placeholder="e.g., Boston, MA or 02101"
                  value={searchForm.location_input}
                  onChange={(e) => handleFormChange('location_input', e.target.value)}
                  disabled={!searchForm.limit_location}
                  InputProps={{
                    startAdornment: <LocationIcon sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  label="Search Radius (miles)"
                  type="number"
                  value={searchForm.radius_miles}
                  onChange={(e) => handleFormChange('radius_miles', parseInt(e.target.value) || 50)}
                  disabled={!searchForm.limit_location}
                  inputProps={{ min: 5, max: 500, step: 5 }}
                />
              </Grid>
            </Grid>

            <Box display="flex" justifyContent="flex-end" alignItems="center" mt={3} flexWrap="wrap" gap={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={searchForm.recruiting_only}
                    onChange={(e) => handleFormChange('recruiting_only', e.target.checked)}
                  />
                }
                label="Recruiting Only"
              />
              <Button
                variant="contained"
                size="large"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={searching}
                sx={{ px: 4 }}
              >
                {searching ? 'Searching...' : 'Search Clinical Trials'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Search Results from ClinicalTrials.gov */}
      {foundTrials.length > 0 && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  Search Results
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Found {foundTrials.length} trials from ClinicalTrials.gov
                </Typography>
              </Box>
            </Box>

            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>NCT ID</strong></TableCell>
                    <TableCell><strong>Trial Title</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Conditions</strong></TableCell>
                    <TableCell align="right"><strong>Links</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {foundTrials.map((trial) => (
                    <TableRow key={trial.id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <ScienceIcon fontSize="small" color="primary" />
                          <Typography variant="body2" fontWeight="medium">
                            {trial.id}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {trial.title || 'Untitled'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={trial.overallStatus}
                          color={trial.overallStatus === 'RECRUITING' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {trial.conditions?.slice(0, 2).join(', ') || 'N/A'}
                          {trial.conditions && trial.conditions.length > 2 && '...'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" gap={1} justifyContent="flex-end">
                          <IconButton
                            size="small"
                            onClick={() => handleSaveAsMatched(trial)}
                            color="secondary"
                            title="Save as Matched"
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenTrial(trial.id)}
                            color="primary"
                            title="View on ClinicalTrials.gov"
                          >
                            <OpenIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Matched Trials Results */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Trial Matching Results Imported from iOS App
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Clinical trials matched to your health profile
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddDialogOpen(true)}
              size="small"
            >
              Add
            </Button>
          </Box>

          {matchedTrials && matchedTrials.length === 0 ? (
            <Box textAlign="center" py={8}>
              <ScienceIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Matched Trials Yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Search for clinical trials above to see your matched results here
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>NCT ID</strong></TableCell>
                    <TableCell><strong>Eligibility Status</strong></TableCell>
                    <TableCell><strong>Matched Date</strong></TableCell>
                    <TableCell><strong>Last Updated</strong></TableCell>
                    <TableCell align="right"><strong>Links</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {matchedTrials?.map((trial) => (
                    <TableRow key={trial.id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <ScienceIcon fontSize="small" color="primary" />
                          <Typography variant="body2" fontWeight="medium">
                            {trial.nct_id}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={trial.eligibility_status || 'Not Analyzed'}
                          size="small"
                          color={getEligibilityColor(trial.eligibility_status)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {dayjs(trial.created_at).format('MMM D, YYYY')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {dayjs(trial.edited_at).format('MMM D, YYYY h:mm A')}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" gap={1} justifyContent="flex-end">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditMatchedTrial(trial)}
                            title="Edit"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteMatchedTrial(trial)}
                            title="Delete"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleOpenTrial(trial.nct_id)}
                            title="View on ClinicalTrials.gov"
                          >
                            <OpenIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Add Matched Trial Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Matched Trial</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="NCT ID"
              placeholder="e.g., NCT12345678"
              value={addForm.nct_id}
              onChange={(e) => setAddForm(prev => ({ ...prev, nct_id: e.target.value.toUpperCase() }))}
              margin="normal"
              helperText="Format: NCT followed by 8 digits"
            />

            <TextField
              select
              fullWidth
              label="Eligibility Status"
              value={addForm.eligibility_status}
              onChange={(e) => setAddForm(prev => ({ ...prev, eligibility_status: e.target.value }))}
              margin="normal"
            >
              <MenuItem value="not analyzed">Not Analyzed</MenuItem>
              <MenuItem value="eligible">Eligible</MenuItem>
              <MenuItem value="potentially eligible">Potentially Eligible</MenuItem>
              <MenuItem value="not eligible">Not Eligible</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddDialogOpen(false)
            setAddForm({ nct_id: '', eligibility_status: 'not analyzed' })
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddMatchedTrial}
          >
            Add Trial
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Matched Trial Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Matched Trial</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="NCT ID"
              value={editingTrial?.nct_id || ''}
              disabled
              margin="normal"
              helperText="NCT ID cannot be changed"
            />

            <TextField
              select
              fullWidth
              label="Eligibility Status"
              value={editForm.eligibility_status}
              onChange={(e) => setEditForm(prev => ({ ...prev, eligibility_status: e.target.value }))}
              margin="normal"
            >
              <MenuItem value="not analyzed">Not Analyzed</MenuItem>
              <MenuItem value="eligible">Eligible</MenuItem>
              <MenuItem value="potentially eligible">Potentially Eligible</MenuItem>
              <MenuItem value="not eligible">Not Eligible</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditDialogOpen(false)
            setEditingTrial(null)
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateMatchedTrial}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default TrialSearches

